import { NODE_DRIFTWOOD, NODE_SPEAR } from '../gather/Nodes';
import { CINDER_PEAK } from '../world/Sky';
import { CC_NODES } from '../world/areas/a01/CrashCoast';
import { DJ_HUT } from '../world/areas/a02/DeepJungle';
import { Hud } from './Hud';
import { bearingCopy, waypointSnap, type WaypointSnap } from './Waypoint';

export const HINT_FADE_MS = Number.POSITIVE_INFINITY;
export const HINT_TEXT =
  'WASD · mouse · Shift · Space · C sneak · E use · LMB attack · B build · Q cycle · F fire';

export type ObjectiveSource = {
  held?: string | null;
  inventory?: { id: string; count: number }[];
  lastDrink?: number | null;
  fireLit?: boolean;
  shelter?: boolean;
  player?: { x: number; y: number; z: number };
  yaw?: number;
  flags?: { knowMeridian?: boolean };
};

export type StepId = 'spear' | 'wood' | 'drink' | 'fire' | 'shelter' | 'mast';

type Step = { id: StepId; label: string };

const STEPS: readonly Step[] = [
  { id: 'spear', label: 'Find the wreck spear' },
  { id: 'wood', label: 'Gather wood' },
  { id: 'drink', label: 'Drink' },
  { id: 'fire', label: 'Light the Ember fire' },
  { id: 'shelter', label: 'Build a lean-to' },
  { id: 'mast', label: 'Read the ridge mast' },
];

const HOLD_EMBER = 'Hold Camp Ember';

type Flags = Record<StepId, boolean>;

let instance: Objectives | null = null;

export class Objectives {
  private readonly flags: Flags = {
    spear: false,
    wood: false,
    drink: false,
    fire: false,
    shelter: false,
    mast: false,
  };
  private root: HTMLElement | null = null;
  private nowEl: HTMLElement | null = null;
  private doneEl: HTMLElement | null = null;
  private hintEl: HTMLElement | null = null;
  private hintAt = 0;
  private faded = false;
  private lastNow = '';
  private lastDone = '';
  private way: WaypointSnap | null = null;

  static tick(ds: ObjectiveSource): void {
    if (!instance) {
      instance = new Objectives();
    }
    instance.step(ds);
  }

  static waypoint(): WaypointSnap | null {
    return instance?.way ?? null;
  }

  private step(ds: ObjectiveSource): void {
    this.ensure();
    this.read(ds);
    this.paint();
    this.pointCinder(ds);
    this.fadeHint();
  }

  private read(ds: ObjectiveSource): void {
    if (ds.held === 'spear') {
      this.flags.spear = true;
    }
    if (countItem(ds, 'wood') >= 2) {
      this.flags.wood = true;
    }
    if (ds.lastDrink != null) {
      this.flags.drink = true;
    }
    if (ds.fireLit) {
      this.flags.fire = true;
    }
    if (ds.shelter) {
      this.flags.shelter = true;
    }
    if (ds.flags?.knowMeridian) {
      this.flags.mast = true;
    }
  }

  private paint(): void {
    const done = STEPS.filter((step) => this.flags[step.id]);
    const current = STEPS.find((step) => !this.flags[step.id]);
    const now = current?.label ?? HOLD_EMBER;
    if (this.nowEl && now !== this.lastNow) {
      this.nowEl.textContent = now;
      this.root?.setAttribute('aria-label', `Objective ${now}`);
      this.lastNow = now;
    }
    const doneKey = done.map((step) => step.id).join(',');
    if (this.doneEl && doneKey !== this.lastDone) {
      this.doneEl.replaceChildren(
        ...done.map((step) => {
          const li = document.createElement('li');
          li.className = 'is-done';
          li.textContent = step.label;
          return li;
        }),
      );
      this.doneEl.hidden = done.length === 0;
      this.lastDone = doneKey;
    }
  }

  private pointCinder(ds: ObjectiveSource): void {
    const x = ds.player?.x ?? 0;
    const z = ds.player?.z ?? 0;
    const yaw = Number.isFinite(ds.yaw) ? (ds.yaw as number) : 0;
    const dest = this.destination();
    this.way = waypointSnap({ x, z }, yaw, dest);
    Hud.set({
      yaw,
      targetYaw: this.way.targetYaw,
      bearing: bearingCopy(this.way),
      bearingSide: this.way.bearing,
    });
  }

  private destination(): { x: number; z: number; label: string } {
    if (!this.flags.spear) {
      return { x: NODE_SPEAR.x, z: NODE_SPEAR.z, label: 'Wreck spear' };
    }
    if (!this.flags.wood) {
      return { x: NODE_DRIFTWOOD.x, z: NODE_DRIFTWOOD.z, label: 'Driftwood' };
    }
    if (!this.flags.drink) {
      return { x: 2, z: 16.5, label: 'Shore water' };
    }
    const ember = CC_NODES['CC-EMBER'] ?? { x: -18, z: -40 };
    if (!this.flags.fire) {
      return { x: ember.x, z: ember.z, label: 'Camp Ember fire' };
    }
    if (!this.flags.shelter) {
      return { x: ember.x + 2.4, z: ember.z, label: 'Lean-to site' };
    }
    if (!this.flags.mast) {
      return { x: DJ_HUT.x, z: DJ_HUT.z, label: 'Ridge mast' };
    }
    return { x: CINDER_PEAK.x, z: CINDER_PEAK.z, label: 'Mount Cinder' };
  }

  private fadeHint(): void {
    const hint = this.hintEl;
    if (!hint) {
      return;
    }
    const hud = document.getElementById('hud');
    if (hud?.hidden) {
      return;
    }
    if (this.hintAt === 0) {
      this.hintAt = performance.now();
    }
    if (!this.faded && performance.now() - this.hintAt >= HINT_FADE_MS) {
      hint.classList.add('is-faded');
      hint.setAttribute('aria-hidden', 'true');
      this.faded = true;
    }
  }

  private ensure(): void {
    const host =
      document.getElementById('hud') ??
      document.getElementById('ui-root') ??
      document.body;

    let root = document.getElementById('objective');
    if (!root) {
      root = document.createElement('div');
      root.id = 'objective';
      root.setAttribute('data-hud', 'objective');
      root.setAttribute('aria-live', 'polite');
      root.setAttribute('aria-label', 'Objective');
      host.append(root);
    }
    this.root = root;

    let nowEl = root.querySelector<HTMLElement>('.objective-now');
    if (!nowEl) {
      nowEl = document.createElement('div');
      nowEl.className = 'objective-now';
      root.prepend(nowEl);
    }
    this.nowEl = nowEl;

    let doneEl = root.querySelector<HTMLElement>('.objective-done');
    if (!doneEl) {
      doneEl = document.createElement('ul');
      doneEl.className = 'objective-done';
      root.append(doneEl);
    }
    this.doneEl = doneEl;

    let hint = document.getElementById('controls-hint');
    if (!hint) {
      hint = document.createElement('div');
      hint.id = 'controls-hint';
      hint.setAttribute('data-hud', 'controls');
      hint.setAttribute('aria-label', 'Controls');
      hint.textContent = HINT_TEXT;
      host.append(hint);
    }
    this.hintEl = hint;
  }
}

export function countItem(ds: ObjectiveSource, id: string): number {
  let total = 0;
  for (const slot of ds.inventory ?? []) {
    if (slot.id === id) {
      total += slot.count;
    }
  }
  return total;
}

/** Yaw that faces Mount Cinder. Forward is (−sin yaw, −cos yaw). */
export function cinderYaw(x: number, z: number): number {
  return Math.atan2(-(CINDER_PEAK.x - x), -(CINDER_PEAK.z - z));
}
